import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../../auth/AuthContext'
import type { UpdateUserRequest, UsuarioDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { clip, onlyLetters } from '../../../utils/inputs'
import { errorMessage, initials, MY_ACCOUNT_BACK_PATH, personName } from '../common/my-account-common'
import type { FormMessage } from '../common/my-account-common'

export interface MyAccountFormState {
    id: number | null
    correo: string
    principal: boolean
    nombres: string
    apPat: string
    apMat: string
    celular: string
}

const LOAD_ERROR = 'No se pudo cargar tu cuenta.'
const SAVE_ERROR = 'Error al guardar los cambios'

const emptyState: MyAccountFormState = {
    id: null,
    correo: '',
    principal: false,
    nombres: '',
    apPat: '',
    apMat: '',
    celular: '',
}

const onlyDigits = (value: string, max: number) => clip(value, max).replace(/\D/g, '')

const toFormState = (body: UsuarioDTO): MyAccountFormState => ({
    id: body.id,
    correo: body.correo || '',
    principal: Boolean(body.principal),
    nombres: body.nombres || '',
    apPat: body.apPat || '',
    apMat: body.apMat || '',
    celular: body.celular || '',
})

const toRequest = (state: MyAccountFormState, id: number): UpdateUserRequest => ({
    id,
    nombres: state.nombres,
    apPat: state.apPat,
    apMat: state.apMat,
    celular: state.celular,
})

export const useMyAccountFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { setUser } = useAuth()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || MY_ACCOUNT_BACK_PATH

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [passwordOpen, setPasswordOpen] = useState(false)
    const [state, setState] = useState<MyAccountFormState>(emptyState)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const response: WrapperResponse<UsuarioDTO> | null = await (await APIInvoke.invokeGET('/usuarios/me')).json()
                if (!active) return
                if (!response?.ok || !response.body) throw new Error(response?.message || LOAD_ERROR)
                setState(toFormState(response.body))
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [])

    const patch = (partial: Partial<MyAccountFormState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        nombres: (value: string) => patch({ nombres: onlyLetters(value, 60) }),
        apPat: (value: string) => patch({ apPat: onlyLetters(value, 45) }),
        apMat: (value: string) => patch({ apMat: onlyLetters(value, 45) }),
        celular: (value: string) => patch({ celular: onlyDigits(value, 10) }),
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!state.id) return
        try {
            setSaving(true)
            setMessage(null)
            const response: WrapperResponse<UsuarioDTO> = await (await APIInvoke.invokePUT('/usuarios', toRequest(state, state.id))).json()
            if (response.ok) {
                setMessage({ type: 'success', message: response.message || 'Cambios guardados correctamente' })
                setUser((current) => (current ? {
                    ...current,
                    nombres: state.nombres,
                    apPat: state.apPat,
                    apMat: state.apMat,
                    celular: state.celular,
                } : current))
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('error al guardar la cuenta', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    const passwordChanged = (text: string) => {
        setPasswordOpen(false)
        setMessage({ type: 'success', message: text })
    }

    const fullName = personName(state)

    return {
        form: state,
        loading,
        saving,
        busy: loading || saving,
        busyLabel: loading ? 'Cargando tu cuenta…' : 'Guardando cambios…',
        message,
        fullName: fullName || 'Usuario',
        initials: initials(`${state.nombres} ${state.apPat}`),
        nameLocked: state.principal,
        backTo: backUrl,
        passwordOpen,
        change,
        save,
        cancel: () => history.push(backUrl),
        openPassword: () => setPasswordOpen(true),
        closePassword: () => setPasswordOpen(false),
        passwordChanged,
    }
}
